import React, { useState } from 'react';
import {
  Divider,
  Link,
  IconTrash16,
  Text,
  Box,
  IconArrowRight16,
  Modal,
  ModalFooter,
  Placeholder,
} from '@stitch-fix/mode-react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { getHideKeptItemFeedback } from './getHideKeptItemFeedback.graphql';
import { hideItemClientFeedback } from './hideItemClientFeedback.graphql';
import HideItemFeedbackForm from '../HideItemFeedbackForm/HideItemFeedbackForm';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import {
  trackRemoveItem,
  trackConfirmRemoveItem,
  trackCancelRemoveItem,
} from '../Gated/src/tracking/hideItemTracker';
import './HideItemLink.module.scss';

import { loadTranslations } from '../Gated/src/shared/i18n';
import translations from './translations.json';

const { useTranslation } = loadTranslations(translations, 'HideItemLink');

const HideItemLink = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});
  const [questions, setQuestions] = useState([]);
  const [hasLoadedQuestions, setHasLoadedQuestions] = useState(false);
  const [errorLoadingQuestions, setErrorLoadingQuestions] = useState(false);

  const [state] = useProductDetailsPageContext();

  const { reportSelectEvent } = useSelectEvent();
  const { t } = useTranslation();

  useQuery(getHideKeptItemFeedback, {
    variables: { skuId: state.productSkuId },
    skip: !modalOpen,
    onCompleted: data => {
      setQuestions(
        data.client.keptItem.hideKeptItemFeedbackQuestions.questions,
      );
      setHasLoadedQuestions(true);
    },
    onError: () => {
      setErrorLoadingQuestions(true);
    },
  });

  const [hideItemClientFeedbackMutation] = useMutation(hideItemClientFeedback);

  const trackingContext = (additional = {}) => {
    return {
      skuId: state.productSkuId,
      source: state.source,
      collection: state.collectionName,
      ...additional,
    };
  };

  const handleOpenModal = () => {
    trackRemoveItem(trackingContext());

    reportSelectEvent({
      schema: 'select',
      name: 'remove_kept_item_link',
      action_name: 'view_remove_kept_item_modal',
    });

    setModalOpen(true);
  };

  const handleCloseModal = () => {
    trackCancelRemoveItem(trackingContext());

    reportSelectEvent({
      schema: 'select',
      name: 'remove_kept_item_modal',
      action_name: 'cancel_remove_kept_item',
    });

    setModalOpen(false);
  };

  const handleHideSuccess = () => {
    const storageKey = 'kept-item-hidden';

    const showItemHiddenToastOnRedirect = () => {
      sessionStorage.setItem(storageKey, true);
    };

    showItemHiddenToastOnRedirect();
    window.location.assign(state.hideItemSuccessPath);
  };

  const handleSubmitRemoveItem = e => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    trackConfirmRemoveItem(trackingContext({ feedback: feedbackData }));

    reportSelectEvent({
      schema: 'select',
      name: 'remove_kept_item_modal',
      action_name: 'confirm_remove_kept_item',
    });

    hideItemClientFeedbackMutation({
      variables: {
        input: {
          skuId: state.productSkuId,
          // TODO: Once we can access orderId from this page, we should send the orderId as resourceId in this call.
          // resourceId: orderId,
          context: 'product_page_ui_my_item',
          ...feedbackData,
        },
      },
      onCompleted: () => {
        handleHideSuccess();
      },
    });
  };

  const renderModalContent = () => {
    if (errorLoadingQuestions) {
      return (
        <Box variant="error-light" p={1}>
          <Text setting="body-large" data-testid="loading-error-message">
            {t('loadingError')}
          </Text>
        </Box>
      );
    }

    return (
      <div data-testid="hide-item-modal">
        <Text color="red-40" as="p" setting="body-small">
          {t('modal.explanation')}
        </Text>
        <Box my={1}>
          <Divider variant="subtle" />
        </Box>
        <p styleName="reason-subtitle">{t('modal.feedbackSubtitle')}</p>
        {hasLoadedQuestions && (
          <HideItemFeedbackForm
            feedbackQuestions={questions}
            bubbleFeedback={setFeedbackData}
          />
        )}
      </div>
    );
  };

  const renderModal = () => {
    const footer = (
      <ModalFooter
        primaryAction={{
          text: t('modal.submit'),
          onClick: handleSubmitRemoveItem,
          disabled: isSubmitting || !hasLoadedQuestions,
          'data-testid': 'hide-item-submit-btn',
        }}
        secondaryAction={{
          text: t('modal.cancel'),
          endIcon: <IconArrowRight16 />,
          onClick: handleCloseModal,
          'data-testid': 'hide-item-cancel-btn',
        }}
      />
    );

    return (
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        heading={t('modal.heading')}
        footer={footer}
      >
        {renderModalContent()}
      </Modal>
    );
  };

  const showPlaceholders = state.loading || !state.shopProduct;

  return showPlaceholders ? (
    <Placeholder width={8} />
  ) : (
    <>
      <Link
        size="tertiary"
        as="button"
        startIcon={<IconTrash16 />}
        onClick={handleOpenModal}
        data-testid="hide-kept-item-link"
        mb={1.5}
      >
        {t('linkText')}
      </Link>
      {renderModal()}
    </>
  );
};

export default HideItemLink;
